import { Layout } from 'antd';
import clsx from 'clsx';
import TextAlert from 'common/components/alert.component';
import Footer from 'common/components/footer.component';
import Header from 'common/components/header.component';
import PreHeader from 'common/components/pre-header.component';
import { TextAlertTypes } from 'config/constants/global.constants';
import { RouteName } from 'config/constants/route.constants';
import { AxiosInterceptor } from 'config/interceptors/axios.interceptor';
import SettingService from 'config/services/setting.service';
import CartUtils from 'config/utils/cart.util';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { Auth } from 'store/models/auth.model';
import { User } from 'store/models/user.model';
import { RootState } from '../store';
import { AuthSlice } from 'store/slices/auth.slice';
import { SettingSlice } from 'store/slices/setting.slice';
import { GeneralTranscription, UserTranscription } from './config/constants/transcription.constant';
import AppRoutes from './config/routes';
import styles from './style.module.scss';

AxiosInterceptor();

const verifyPathToWatchCompactContainer = (pathname: string): boolean => {
  const pathWithCompactContainer: string[] = [RouteName.HOME, RouteName.SEARCH];
  return !pathWithCompactContainer.includes(pathname);
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [isCompactContainer, setIsCompactContainer] = useState<boolean>(false);
  const {
    auth,
    user: { userProfile },
    search: { searchTerm },
    catalog: { products = [] },
  } = useSelector((state: RootState) => state);
  const isSplit = useMemo(() => searchTerm !== '' && products.length > 0, [searchTerm, products]);

  const renderCompleteProfileDataAlert = useCallback((auth: Auth, user: User): React.ReactNode => {
    const showAlert = !auth.isFetching && auth.isAuthenticated && !user.name;
    if (showAlert) {
      const textAlert = UserTranscription.COMPLETE_PROFILE_DATA_TEXT.replace('#mail', user.email || GeneralTranscription.USER);
      return <TextAlert text={textAlert} type={TextAlertTypes.WARNING} likeBanner showIcon />;
    }
    return null;
  }, []);

  useEffect(() => {
    dispatch(AuthSlice.refreshSessionExpired(false));
    CartUtils.hydrateCart({ skipPush: location.pathname === RouteName.PURCHASE_RESULT });
  }, []);

  const loadHomeSettings = useCallback(async () => {
    try {
      const homeResponse = await SettingService.getHome();
      dispatch(SettingSlice.refreshHomeIslands(homeResponse.homeIslands || []));
      if (homeResponse.countryDisplay) {
        dispatch(SettingSlice.refreshCountryDisplay(homeResponse.countryDisplay));
      }
    } catch {
      // Home page surfaces its own errors when visited
    }
  }, [dispatch]);

  useEffect(() => {
    loadHomeSettings();
  }, [loadHomeSettings]);

  useEffect(() => {
    setIsCompactContainer(verifyPathToWatchCompactContainer(location.pathname));
  }, [location.pathname]);

  return (
    <Layout>
      <PreHeader />
      {renderCompleteProfileDataAlert(auth, userProfile)}
      <Header />
      <Layout.Content
        className={clsx(styles['site-layout'], isSplit && styles['split'], isCompactContainer && styles['compact-container'])}
      >
        <AppRoutes />
      </Layout.Content>
      <Footer />
    </Layout>
  );
};

export const App: React.FC = () => (
  <Router>
    <AppContent />
  </Router>
);
